import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/** Paleta de cores para o PDF */
const PDF_COLORS = {
    primaryDark: [30, 41, 59],       // slate-800
    primaryMid: [51, 65, 85],        // slate-700
    accent: [99, 102, 241],          // indigo-500
    accentLight: [165, 180, 252],    // indigo-300
    danger: [239, 68, 68],           // red-500
    dangerLight: [254, 226, 226],    // red-100
    warning: [245, 158, 11],         // amber-500
    warningLight: [254, 243, 199],   // amber-100
    blue: [59, 130, 246],            // blue-500
    blueLight: [219, 234, 254],      // blue-100
    white: [255, 255, 255],
    textPrimary: [15, 23, 42],       // slate-900
    textSecondary: [100, 116, 139],  // slate-500
    borderColor: [226, 232, 240],    // slate-200
    bgLight: [248, 250, 252],        // slate-50
    bgMid: [241, 245, 249],          // slate-100
};

const PAGE_W = 210; // A4 largura mm
const PAGE_H = 297; // A4 altura mm
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;

/** Remove caracteres especiais (emojis) não-Latin1 para o jsPDF */
function sanitizeText(text) {
    if (!text) return '';
    return String(text)
        .replace(/[“”]/g, '"') // aspas curvas
        .replace(/[‘’]/g, "'") // aspas simples curvas
        .replace(/[—–]/g, '-') // travessões
        .replace(/…/g, '...')  // reticências
        .replace(/[^\x00-\xFF]/g, '') // remove todos caracteres fora do Latin1 (emojis)
        .trim();
}

/** Cria nova página e retorna Y inicial */
function newPage(doc) {
    doc.addPage();
    return MARGIN;
}

/** Desenha cabeçalho padrão em cada página */
function drawPageHeader(doc, pageNum) {
    // Faixa superior
    doc.setFillColor(...PDF_COLORS.primaryDark);
    doc.rect(0, 0, PAGE_W, 10, 'F');
    doc.setFontSize(7);
    doc.setTextColor(...PDF_COLORS.accentLight);
    doc.setFont('helvetica', 'normal');
    doc.text('Diagnóstico de Retenção de Clientes - Relatório IA', MARGIN, 6.5);
    doc.text(`Página ${pageNum}`, PAGE_W - MARGIN, 6.5, { align: 'right' });
}

/** Desenha rodapé padrão em cada página */
function drawPageFooter(doc, generatedAt) {
    const y = PAGE_H - 7;
    doc.setDrawColor(...PDF_COLORS.borderColor);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 2, PAGE_W - MARGIN, y - 2);
    doc.setFontSize(6.5);
    doc.setTextColor(...PDF_COLORS.textSecondary);
    doc.text('Gerado automaticamente pelo sistema de gestão de projetos', MARGIN, y + 0.5);
    if (generatedAt) {
        doc.text(`Análise de ${new Date(generatedAt).toLocaleString('pt-BR')}`, PAGE_W - MARGIN, y + 0.5, { align: 'right' });
    }
}

/** Quebra texto em linhas com largura máxima */
function splitText(doc, text, maxWidth, fontSize) {
    doc.setFontSize(fontSize);
    return doc.splitTextToSize(String(text), maxWidth);
}

/** Verifica se há espaço suficiente, senão quebra página */
function ensureSpace(doc, y, needed, pageNum, generatedAt) {
    if (y + needed > PAGE_H - 18) {
        drawPageFooter(doc, generatedAt);
        pageNum++;
        y = newPage(doc);
        drawPageHeader(doc, pageNum);
        y = 16; // abaixo do header
    }
    return { y, pageNum };
}

/** Converte elemento DOM em imagem e insere no PDF, preservando a relação de aspecto */
async function insertElementAsImage(doc, element, y, maxH, opts = {}) {
    const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        ...opts,
    });
    const imgData = canvas.toDataURL('image/png');
    const ratio = canvas.width / canvas.height; // largura / altura do canvas
    // Calcula dimensões respeitando o aspecto original
    let w = CONTENT_W;
    let h = w / ratio;
    if (h > maxH) {
        // Restringe a altura e reduz a largura proporcionalmente
        h = maxH;
        w = h * ratio;
    }
    // Centraliza horizontalmente se w < CONTENT_W
    const x = MARGIN + (CONTENT_W - w) / 2;
    doc.addImage(imgData, 'PNG', x, y, w, h);
    return h;
}

/** Renderiza seção de título no PDF */
function renderSectionTitle(doc, title, y, color = PDF_COLORS.accent) {
    doc.setFillColor(...color);
    doc.rect(MARGIN, y, 4, 6, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.textPrimary);
    doc.text(title, MARGIN + 7, y + 4.5);
    return y + 10;
}

/** Renderiza KPI cards lado a lado */
function renderKpiCards(doc, biStats, y) {
    const { churnRate, previousChurnRate, avgChurn, churnReasons, earlyChurn } = biStats;
    const totalChurns = (churnReasons || []).reduce((a, c) => a + c.value, 0);
    const totalEarlyChurns = (earlyChurn || []).reduce((a, e) => a + e.cancelamentos, 0);
    const earlyChurnPct = totalChurns > 0 ? ((totalEarlyChurns / totalChurns) * 100).toFixed(1) : '0.0';
    const variation = previousChurnRate != null ? churnRate - previousChurnRate : null;

    const cards = [
        {
            label: 'Taxa de Churn (Último Mês)',
            value: `${churnRate.toFixed(2)}%`,
            sub: variation != null ? `${variation > 0 ? '+' : ''}${variation.toFixed(2)}% vs mês ant.` : null,
            subColor: variation > 0 ? PDF_COLORS.danger : [34, 197, 94],
            bg: PDF_COLORS.dangerLight,
            accent: PDF_COLORS.danger,
        },
        {
            label: 'Churn Médio (12 Meses)',
            value: `${(avgChurn || 0).toFixed(2)}%`,
            sub: 'Média de evasão anual',
            subColor: PDF_COLORS.textSecondary,
            bg: PDF_COLORS.blueLight,
            accent: PDF_COLORS.blue,
        },
        {
            label: 'Early Churn (Fidelidade)',
            value: totalEarlyChurns.toLocaleString('pt-BR'),
            sub: `${earlyChurnPct}% do total de churns`,
            subColor: PDF_COLORS.warning,
            bg: PDF_COLORS.warningLight,
            accent: PDF_COLORS.warning,
        },
    ];

    const cardW = (CONTENT_W - 6) / 3;
    const cardH = 26;

    cards.forEach((card, i) => {
        const x = MARGIN + i * (cardW + 3);
        // Card bg
        doc.setFillColor(...card.bg);
        doc.roundedRect(x, y, cardW, cardH, 2.5, 2.5, 'F');
        // Barra lateral colorida
        doc.setFillColor(...card.accent);
        doc.roundedRect(x, y, 3, cardH, 1.5, 1.5, 'F');
        // Label
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text(card.label, x + 6, y + 6.5);
        // Valor
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text(card.value, x + 6, y + 15);
        // Sub-label
        if (card.sub) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...card.subColor);
            doc.text(card.sub, x + 6, y + 21);
        }
    });

    return y + cardH + 5;
}

/** Renderiza tabela genérica */
function renderTable(doc, headers, rows, y, colWidths) {
    const rowH = 6.5;
    const totalW = colWidths.reduce((a, b) => a + b, 0);

    // Header
    doc.setFillColor(...PDF_COLORS.primaryDark);
    doc.rect(MARGIN, y, totalW, rowH, 'F');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.white);
    let xCursor = MARGIN + 2;
    headers.forEach((h, i) => {
        doc.text(h.label, xCursor, y + 4.5, { align: h.align || 'left', maxWidth: colWidths[i] - 3 });
        xCursor += colWidths[i];
    });
    y += rowH;

    rows.forEach((row, rowIdx) => {
        // Zebra striping
        if (rowIdx % 2 === 0) {
            doc.setFillColor(...PDF_COLORS.bgLight);
            doc.rect(MARGIN, y, totalW, rowH, 'F');
        }
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        xCursor = MARGIN + 2;
        row.forEach((cell, ci) => {
            const align = headers[ci]?.align || 'left';
            const textX = align === 'right' ? xCursor + colWidths[ci] - 3 : xCursor;
            if (cell?.color) {
                doc.setTextColor(...cell.color);
                doc.text(String(cell.text ?? ''), textX, y + 4.5, { align, maxWidth: colWidths[ci] - 3 });
                doc.setTextColor(...PDF_COLORS.textPrimary);
            } else {
                doc.text(String(cell ?? ''), textX, y + 4.5, { align, maxWidth: colWidths[ci] - 3 });
            }
            xCursor += colWidths[ci];
        });
        // Linha separadora
        doc.setDrawColor(...PDF_COLORS.borderColor);
        doc.setLineWidth(0.2);
        doc.line(MARGIN, y + rowH, MARGIN + totalW, y + rowH);
        y += rowH;
    });

    return y + 3;
}

/** Renderiza mini gráfico de barras horizontais in-PDF sem canvas */
function renderMiniBarChart(doc, data, y, valueKey, labelKey, colorFn, maxW = CONTENT_W) {
    const maxVal = Math.max(...data.map(d => d[valueKey] || 0));
    const barH = 5.5;
    const gap = 1.5;
    const labelW = 55;
    const valW = 18;
    const barW = maxW - labelW - valW - 4;

    data.forEach((item, i) => {
        const val = item[valueKey] || 0;
        const filledW = maxVal > 0 ? (val / maxVal) * barW : 0;
        const color = colorFn(i);

        // Label
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        const label = String(item[labelKey] || '');
        const shortLabel = label.length > 20 ? label.slice(0, 20) + '…' : label;
        doc.text(shortLabel, MARGIN, y + barH - 1.5);

        // Fundo da barra
        doc.setFillColor(...PDF_COLORS.bgMid);
        doc.roundedRect(MARGIN + labelW, y, barW, barH, 1, 1, 'F');

        // Barra preenchida
        if (filledW > 0) {
            doc.setFillColor(...color);
            doc.roundedRect(MARGIN + labelW, y, filledW, barH, 1, 1, 'F');
        }

        // Valor
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text(val.toFixed ? val.toFixed(2) : String(val), MARGIN + labelW + barW + 3, y + barH - 1.5);

        y += barH + gap;
    });

    return y + 3;
}

/** Renderiza mini gráfico de rosca (pizza) para motivos de cancelamento */
function renderDonutChart(doc, data, y, cx, cy, r) {
    const total = data.reduce((s, d) => s + d.value, 0);
    const DONUT_COLORS = [
        [239, 68, 68], [249, 115, 22], [234, 179, 8], [132, 204, 22],
        [34, 197, 94], [6, 182, 212], [59, 130, 246], [139, 92, 246],
        [217, 70, 239], [244, 63, 94],
    ];

    let startAngle = -Math.PI / 2;
    data.forEach((item, i) => {
        const slice = (item.value / total) * 2 * Math.PI;
        const endAngle = startAngle + slice;
        const color = DONUT_COLORS[i % DONUT_COLORS.length];
        // jsPDF não tem arc nativo para setores - usamos polígono aproximado
        const points = [];
        const steps = Math.max(8, Math.round(slice * 20));
        const innerR = r * 0.52;
        for (let s = 0; s <= steps; s++) {
            const a = startAngle + (slice * s) / steps;
            points.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
        }
        for (let s = steps; s >= 0; s--) {
            const a = startAngle + (slice * s) / steps;
            points.push([cx + Math.cos(a) * innerR, cy + Math.sin(a) * innerR]);
        }

        doc.setFillColor(...color);
        doc.setDrawColor(...PDF_COLORS.white);
        doc.setLineWidth(0.4);

        // Desenha o setor como polígono
        if (points.length > 2) {
            const path = points.map((p, idx) =>
                `${idx === 0 ? 'M' : 'L'} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' ') + ' Z';
            try {
                doc.path(path, 'FD');
            } catch (e) {
                // fallback: apenas preenchimento sem stroke
                doc.setDrawColor(...color);
                doc.path(path, 'F');
            }
        }
        startAngle = endAngle;
    });

    // Círculo interior branco para efeito donut
    doc.setFillColor(...PDF_COLORS.white);
    doc.circle(cx, cy, r * 0.52, 'F');

    // Legenda
    const legendX = cx + r + 8;
    let legendY = cy - (data.length * 5) / 2;
    data.slice(0, 8).forEach((item, i) => {
        const color = DONUT_COLORS[i % DONUT_COLORS.length];
        const pct = total > 0 ? ((item.value / total) * 100).toFixed(0) : '0';
        doc.setFillColor(...color);
        doc.rect(legendX, legendY - 2.5, 4, 4, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        const name = item.name.length > 22 ? item.name.slice(0, 22) + '…' : item.name;
        doc.text(`${name} (${pct}%)`, legendX + 6, legendY + 0.5);
        legendY += 5.5;
    });
}

/** Renderiza mini-gráfico de linhas inline (timeline) */
function renderLineChart(doc, data, y, h, w = CONTENT_W) {
    if (!data || data.length < 2) return y + h;
    const maxC = Math.max(...data.map(d => d.cancelamentos || 0));
    const maxV = Math.max(...data.map(d => d.vendas || 0));
    const maxY = Math.max(maxC, maxV, 1);

    const chartX = MARGIN;
    const chartY = y;
    const chartW = w;
    const chartH = h;

    // Fundo
    doc.setFillColor(...PDF_COLORS.bgLight);
    doc.rect(chartX, chartY, chartW, chartH, 'F');

    // Grid lines horizontais
    doc.setDrawColor(...PDF_COLORS.borderColor);
    doc.setLineWidth(0.2);
    [0.25, 0.5, 0.75].forEach(pct => {
        const gy = chartY + (1 - pct) * chartH;
        doc.setLineDashPattern([2, 2], 0);
        doc.line(chartX, gy, chartX + chartW, gy);
    });
    doc.setLineDashPattern([], 0);

    const ptX = (i) => chartX + (i / (data.length - 1)) * chartW;
    const ptY = (v) => chartY + chartH - (v / maxY) * chartH;

    // Linha cancelamentos (vermelho)
    doc.setDrawColor(239, 68, 68);
    doc.setLineWidth(0.8);
    data.forEach((d, i) => {
        if (i === 0) return;
        doc.line(ptX(i - 1), ptY(data[i - 1].cancelamentos), ptX(i), ptY(d.cancelamentos));
    });

    // Linha vendas (azul, tracejado)
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.6);
    doc.setLineDashPattern([2, 2], 0);
    data.forEach((d, i) => {
        if (i === 0) return;
        doc.line(ptX(i - 1), ptY(data[i - 1].vendas || 0), ptX(i), ptY(d.vendas || 0));
    });
    doc.setLineDashPattern([], 0);

    // Labels eixo X (abreviado)
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_COLORS.textSecondary);
    const step = Math.max(1, Math.floor(data.length / 8));
    data.forEach((d, i) => {
        if (i % step !== 0) return;
        const lbl = String(d.month || '').slice(0, 6);
        doc.text(lbl, ptX(i), chartY + chartH + 4, { align: 'center' });
    });

    // Legenda
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setFillColor(239, 68, 68);
    doc.rect(chartX + chartW - 55, chartY + 3, 4, 3, 'F');
    doc.setTextColor(...PDF_COLORS.textPrimary);
    doc.text('Cancelamentos', chartX + chartW - 49, chartY + 5.5);

    doc.setFillColor(59, 130, 246);
    doc.rect(chartX + chartW - 55, chartY + 9, 4, 3, 'F');
    doc.text('Novas Ativações', chartX + chartW - 49, chartY + 11.5);

    return y + chartH + 8;
}

/** Processa e formata o markdown da análise IA em blocos */
function parseMarkdownBlocks(text) {
    if (!text) return [];
    const lines = text.split('\n');
    const blocks = [];
    let currentParagraph = [];

    const flushParagraph = () => {
        if (currentParagraph.length > 0) {
            blocks.push({ type: 'paragraph', text: currentParagraph.join(' ').trim() });
            currentParagraph = [];
        }
    };

    lines.forEach(line => {
        if (line.startsWith('### ') || line.startsWith('## ')) {
            flushParagraph();
            blocks.push({ type: 'h3', text: line.replace(/^#{2,4}\s+/, '') });
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
            flushParagraph();
            blocks.push({ type: 'bullet', text: line.replace(/^[-*]\s+/, '') });
        } else if (line.startsWith('**') && line.endsWith('**')) {
            flushParagraph();
            blocks.push({ type: 'bold', text: line.replace(/\*\*/g, '') });
        } else if (line.trim() === '') {
            flushParagraph();
        } else {
            currentParagraph.push(line);
        }
    });
    flushParagraph();
    return blocks;
}

/**
 * Função principal de exportação do PDF de churn
 * @param {Object} biStats - Dados de BI (churn stats)
 * @param {Object|null} aiData - Dados da análise IA
 * @param {Array|null} chartElementIds - IDs dos elementos DOM dos gráficos
 */
export async function exportChurnPDF(biStats, aiData, chartElementIds = {}) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const generatedAt = aiData?.generatedAt;
    let pageNum = 1;
    let y = MARGIN;

    // ── CAPA ──────────────────────────────────────────────────────────────────
    // Fundo escuro
    doc.setFillColor(...PDF_COLORS.primaryDark);
    doc.rect(0, 0, PAGE_W, 80, 'F');
    // Faixa accent
    doc.setFillColor(...PDF_COLORS.accent);
    doc.rect(0, 70, PAGE_W, 4, 'F');

    // Ícone decorativo desenhado (círculo + raio — sem emoji)
    const iconCX = PAGE_W / 2;
    const iconCY = 30;
    doc.setFillColor(...PDF_COLORS.accent);
    doc.circle(iconCX, iconCY, 10, 'F');
    doc.setFillColor(...PDF_COLORS.white);
    // Raio (seta diagonal simplificada) usando polígono
    const bolt = [
        [iconCX - 1, iconCY - 7], [iconCX + 3, iconCY - 1], [iconCX, iconCY - 1],
        [iconCX + 1, iconCY + 7], [iconCX - 3, iconCY + 1], [iconCX, iconCY + 1],
    ];
    const boltPath = bolt.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' ') + ' Z';
    doc.path(boltPath, 'F');

    // Título principal
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.white);
    doc.text('Diagnóstico de Retenção', PAGE_W / 2, 50, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text('Análise Inteligente de Churn - IA', PAGE_W / 2, 58, { align: 'center' });
    doc.setFontSize(9);
    doc.setTextColor(...PDF_COLORS.accentLight);
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, PAGE_W / 2, 66, { align: 'center' });

    y = 90;

    // Descrição do relatório (sem soft-hyphen, sem quebras manuais — splitTextToSize cuida disso)
    const coverDesc = 'Este relatório consolida os principais indicadores de churn e cancelamentos da base de clientes, acompanhado de diagnóstico gerado por inteligência artificial. As análises incluem distribuição de cancelamentos por plano, cidade, motivos e sazonalidade, com recomendações estratégicas de retenção.';
    doc.setFillColor(...PDF_COLORS.bgLight);
    doc.roundedRect(MARGIN, y, CONTENT_W, 22, 2, 2, 'F');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_COLORS.textSecondary);
    const coverLines = doc.splitTextToSize(coverDesc, CONTENT_W - 8);
    doc.text(coverLines, MARGIN + 4, y + 6);
    y += 28;

    // ── KPI CARDS ─────────────────────────────────────────────────────────────
    y = renderSectionTitle(doc, 'Indicadores Principais', y);
    y = renderKpiCards(doc, biStats, y);
    y += 4;

    // ── GRÁFICO: EVOLUÇÃO HISTÓRICA (canvas do DOM) ───────────────────────────
    const timelineEl = chartElementIds.timeline && document.getElementById(chartElementIds.timeline);
    if (timelineEl) {
        const needed = 70;
        ({ y, pageNum } = ensureSpace(doc, y, needed, pageNum, generatedAt));
        y = renderSectionTitle(doc, 'Evolução de Cancelamentos (Histórico)', y);
        try {
            const h = await insertElementAsImage(doc, timelineEl, y, 62);
            y += h + 6;
        } catch (e) {
            // Fallback para versão inline
            y = renderLineChart(doc, biStats.timeline, y, 55);
        }
    } else {
        ({ y, pageNum } = ensureSpace(doc, y, 70, pageNum, generatedAt));
        y = renderSectionTitle(doc, 'Evolução de Cancelamentos (Histórico)', y);
        y = renderLineChart(doc, biStats.timeline, y, 55);
    }

    // ── GRÁFICO: MOTIVOS DE CANCELAMENTO ─────────────────────────────────────
    ({ y, pageNum } = ensureSpace(doc, y, 75, pageNum, generatedAt));
    const reasonsEl = chartElementIds.reasons && document.getElementById(chartElementIds.reasons);
    if (reasonsEl) {
        y = renderSectionTitle(doc, 'Motivos de Cancelamento', y);
        try {
            const h = await insertElementAsImage(doc, reasonsEl, y, 68);
            y += h + 6;
        } catch (e) {
            y = renderSectionTitle(doc, 'Motivos de Cancelamento', y);
            renderDonutChart(doc, biStats.churnReasons, y, MARGIN + 35, y + 32, 28);
            y += 68;
        }
    } else {
        y = renderSectionTitle(doc, 'Motivos de Cancelamento', y);
        renderDonutChart(doc, biStats.churnReasons, y, MARGIN + 35, y + 32, 28);
        y += 68;
    }

    // ── GRÁFICO: CHURN POR PLANO ──────────────────────────────────────────────
    if (biStats.churnByPlan?.length > 0) {
        const planEl = chartElementIds.plan && document.getElementById(chartElementIds.plan);
        const neededPlan = 18 + biStats.churnByPlan.length * 7.5 + 16;
        ({ y, pageNum } = ensureSpace(doc, y, neededPlan, pageNum, generatedAt));
        y = renderSectionTitle(doc, 'Churn por Plano (por 1.000 assinantes)', y);

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text('Cancelamentos por 1.000 assinantes ativos — últimos 365 dias. Planos com menos de 50 assinantes ativos excluídos.', MARGIN, y);
        y += 5;

        if (planEl) {
            try {
                const h = await insertElementAsImage(doc, planEl, y, 70);
                y += h + 4;
            } catch (e) {
                const PLAN_COLORS = [
                    [239, 68, 68], [249, 115, 22], [234, 179, 8], [132, 204, 22],
                    [34, 197, 94], [6, 182, 212], [59, 130, 246], [139, 92, 246],
                    [217, 70, 239], [244, 63, 94],
                ];
                y = renderMiniBarChart(doc, biStats.churnByPlan, y, 'churnPer1k', 'fullName',
                    (i) => PLAN_COLORS[i % PLAN_COLORS.length]);
            }
        } else {
            const PLAN_COLORS = [
                [239, 68, 68], [249, 115, 22], [234, 179, 8], [132, 204, 22],
                [34, 197, 94], [6, 182, 212], [59, 130, 246], [139, 92, 246],
                [217, 70, 239], [244, 63, 94],
            ];
            y = renderMiniBarChart(doc, biStats.churnByPlan, y, 'churnPer1k', 'fullName',
                (i) => PLAN_COLORS[i % PLAN_COLORS.length]);
        }

        // Tabela resumo churn por plano
        ({ y, pageNum } = ensureSpace(doc, y, 16 + biStats.churnByPlan.length * 6.5 + 10, pageNum, generatedAt));
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text('Tabela Detalhada — Churn por Plano', MARGIN, y);
        y += 4;

        const planHeaders = [
            { label: 'Plano', align: 'left' },
            { label: 'Cancelamentos', align: 'right' },
            { label: 'Assinantes', align: 'right' },
            { label: 'Churn/1k', align: 'right' },
            { label: 'Receita Perdida', align: 'right' },
        ];
        const planColWidths = [58, 28, 28, 24, 44];
        const planRows = biStats.churnByPlan.map(p => [
            String(p.fullName || p.name || '').length > 30
                ? String(p.fullName || p.name || '').slice(0, 30) + '…'
                : String(p.fullName || p.name || ''),
            { text: p.churnCount, color: PDF_COLORS.danger },
            p.activeCount?.toLocaleString('pt-BR') || '—',
            { text: p.churnPer1k?.toFixed(2) || '—', color: PDF_COLORS.warning },
            { text: `R$ ${(p.receitaPerdida || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, color: PDF_COLORS.danger },
        ]);
        y = renderTable(doc, planHeaders, planRows, y, planColWidths);
    }

    // ── GRÁFICO: EARLY CHURN ──────────────────────────────────────────────────
    if (biStats.earlyChurn?.length > 0) {
        const earlyEl = chartElementIds.early && document.getElementById(chartElementIds.early);
        const neededEarly = 80;
        ({ y, pageNum } = ensureSpace(doc, y, neededEarly, pageNum, generatedAt));
        y = renderSectionTitle(doc, 'Early Churn — Distribuição por Mês de Vigência', y, PDF_COLORS.warning);

        const totalEarlyChurns = biStats.earlyChurn.reduce((a, e) => a + e.cancelamentos, 0);
        const totalChurns = biStats.churnReasons.reduce((a, c) => a + c.value, 0);
        const earlyChurnPct = totalChurns > 0 ? ((totalEarlyChurns / totalChurns) * 100).toFixed(1) : '0.0';

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text(`Cancelamentos dentro da fidelidade (12 meses). Total: ${totalEarlyChurns.toLocaleString('pt-BR')} (${earlyChurnPct}% do total de churns).`, MARGIN, y);
        y += 5;

        if (earlyEl) {
            try {
                const h = await insertElementAsImage(doc, earlyEl, y, 60);
                y += h + 6;
            } catch (e) {
                y = renderMiniBarChart(doc, biStats.earlyChurn, y, 'cancelamentos', 'label',
                    (i) => {
                        const intensity = Math.round(255 - (i / 12) * 130);
                        return [intensity, Math.round(80 + i * 8), 68];
                    });
            }
        } else {
            y = renderMiniBarChart(doc, biStats.earlyChurn, y, 'cancelamentos', 'label',
                (i) => {
                    const intensity = Math.round(255 - (i / 12) * 130);
                    return [intensity, Math.round(80 + i * 8), 68];
                });
        }
    }

    // ── GRÁFICO: CHURN POR CIDADE ─────────────────────────────────────────────
    if (biStats.churnByCidade?.length > 0) {
        const cityEl = chartElementIds.city && document.getElementById(chartElementIds.city);
        const neededCity = 20 + biStats.churnByCidade.length * 7.5 + 16;
        ({ y, pageNum } = ensureSpace(doc, y, neededCity, pageNum, generatedAt));
        y = renderSectionTitle(doc, 'Churn por Cidade (Proporcional à População)', y, PDF_COLORS.blue);

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text('Cancelamentos por mil habitantes — últimos 365 dias. Compara impacto proporcional entre cidades.', MARGIN, y);
        y += 5;

        if (cityEl) {
            try {
                const h = await insertElementAsImage(doc, cityEl, y, 60);
                y += h + 4;
            } catch (e) {
                const CITY_C = [[59, 130, 246], [139, 92, 246], [6, 182, 212], [16, 185, 129], [245, 158, 11]];
                y = renderMiniBarChart(doc, biStats.churnByCidade, y, 'churnPer1k', 'name',
                    (i) => CITY_C[i % CITY_C.length]);
            }
        } else {
            const CITY_C = [[59, 130, 246], [139, 92, 246], [6, 182, 212], [16, 185, 129], [245, 158, 11]];
            y = renderMiniBarChart(doc, biStats.churnByCidade, y, 'churnPer1k', 'name',
                (i) => CITY_C[i % CITY_C.length]);
        }

        // Tabela por cidade
        ({ y, pageNum } = ensureSpace(doc, y, 16 + biStats.churnByCidade.length * 6.5 + 6, pageNum, generatedAt));
        const cityHeaders = [
            { label: 'Cidade', align: 'left' },
            { label: 'População', align: 'right' },
            { label: 'Cancelamentos', align: 'right' },
            { label: '/mil hab.', align: 'right' },
            { label: '% Total', align: 'right' },
        ];
        const cityColWidths = [55, 33, 33, 28, 33];
        const cityRows = biStats.churnByCidade.map(c => [
            c.name,
            c.population?.toLocaleString('pt-BR') || '—',
            { text: c.churnCount, color: PDF_COLORS.danger },
            c.churnPer1k?.toFixed(2) || '—',
            `${c.churnPct?.toFixed(1) || '0'}%`,
        ]);
        y = renderTable(doc, cityHeaders, cityRows, y, cityColWidths);
    }

    // ── ANÁLISE IA ────────────────────────────────────────────────────────────
    if (aiData?.content) {
        // Nova página para a seção de IA
        drawPageFooter(doc, generatedAt);
        pageNum++;
        y = newPage(doc);
        drawPageHeader(doc, pageNum);
        y = 16;

        // Banner da seção IA — losango decorativo desenhado (sem emoji)
        doc.setFillColor(...PDF_COLORS.primaryDark);
        doc.roundedRect(MARGIN, y, CONTENT_W, 18, 2, 2, 'F');
        // Losango pequeno como ícone
        const dX = MARGIN + 7;
        const dY = y + 9;
        const dS = 3.5;
        const diamondPath = `M ${dX} ${(dY - dS).toFixed(2)} L ${(dX + dS).toFixed(2)} ${dY.toFixed(2)} L ${dX} ${(dY + dS).toFixed(2)} L ${(dX - dS).toFixed(2)} ${dY.toFixed(2)} Z`;
        doc.setFillColor(...PDF_COLORS.accent);
        doc.path(diamondPath, 'F');
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.white);
        doc.text('Diagnóstico Inteligente - IA', MARGIN + 14, y + 8);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.accentLight);
        doc.text('Análise gerada por Inteligência Artificial com base nos dados de retenção', MARGIN + 14, y + 14);
        y += 22;

        if (aiData.generatedAt) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...PDF_COLORS.textSecondary);
            doc.text(`Análise gerada em: ${new Date(aiData.generatedAt).toLocaleString('pt-BR')}`, MARGIN, y);
            y += 6;
        }

        // Renderiza blocos do markdown
        const blocks = parseMarkdownBlocks(aiData.content);
        for (const block of blocks) {
            if (block.type === 'h3') {
                const neededH = 18;
                ({ y, pageNum } = ensureSpace(doc, y, neededH, pageNum, generatedAt));
                // Espaço acima antes da linha decorativa
                y += 3;
                // Linha decorativa
                doc.setFillColor(...PDF_COLORS.accent);
                doc.rect(MARGIN, y, CONTENT_W, 0.6, 'F');
                // Espaço entre linha e texto da linha seguinte
                y += 7;
                doc.setFontSize(10.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...PDF_COLORS.accent);
                const cleanH3 = sanitizeText(block.text.replace(/[*#]/g, ''));
                const lines = splitText(doc, cleanH3, CONTENT_W, 10.5);
                doc.text(lines, MARGIN, y);
                y += lines.length * 5 + 3;
            } else if (block.type === 'bold') {
                ({ y, pageNum } = ensureSpace(doc, y, 8, pageNum, generatedAt));
                doc.setFontSize(8.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...PDF_COLORS.textPrimary);
                const lines = splitText(doc, sanitizeText(block.text), CONTENT_W, 8.5);
                doc.text(lines, MARGIN, y);
                y += lines.length * 4.5 + 2;
            } else if (block.type === 'bullet') {
                const cleanText = sanitizeText(block.text.replace(/\*\*/g, ''));
                const lines = splitText(doc, cleanText, CONTENT_W - 8, 8);
                const needed = lines.length * 4.5 + 2;
                ({ y, pageNum } = ensureSpace(doc, y, needed, pageNum, generatedAt));
                // Marcador colorido
                doc.setFillColor(...PDF_COLORS.accent);
                doc.circle(MARGIN + 2, y - 1, 1.2, 'F');
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...PDF_COLORS.textPrimary);
                doc.text(lines, MARGIN + 6, y);
                y += needed;
            } else if (block.type === 'paragraph') {
                const cleanText = sanitizeText(block.text.replace(/\*\*/g, ''));
                const lines = splitText(doc, cleanText, CONTENT_W, 8);
                const needed = lines.length * 4.5 + 3;
                ({ y, pageNum } = ensureSpace(doc, y, needed, pageNum, generatedAt));
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...PDF_COLORS.textPrimary);
                doc.text(lines, MARGIN, y);
                y += needed;
            }
        }
    }

    // ── RODAPÉ EM TODAS AS PÁGINAS ────────────────────────────────────────────
    drawPageFooter(doc, generatedAt);

    // Adiciona header e footer nas páginas anteriores retroativamente
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        if (p > 1) drawPageHeader(doc, p); // página 1 tem capa
        drawPageFooter(doc, generatedAt);
    }

    // Salva
    const dateStr = new Date().toISOString().slice(0, 10);
    doc.save(`diagnostico-churn-${dateStr}.pdf`);
}
